import React, { Children, cloneElement } from "react";
import type { ReactNode } from "react";
import type { TabPanelProps } from "@reach/tabs";
import { TabPanel as ReachTabPanel } from "@reach/tabs";

function withRefreshKey(
  children: ReactNode,
  refreshKey: string | number | undefined
) {
  return Children.map(children, (child) =>
    child &&
    typeof child !== "string" &&
    typeof child !== "number" &&
    typeof child !== "boolean" &&
    typeof child === "object" &&
    child !== null &&
    "type" in child
      ? cloneElement(child, { key: refreshKey })
      : child
  );
}

/**
 * Custom TabPanel that ensures that a key is added to Child elements.
 * This ensures that on tab visible, it's data is refreshed.
 * refreshKey should be a piece of state that is updated in the parent when the
 * tab changes.
 * @see https://github.com/reach/reach-ui/issues/828
 */
export const CustomTabPanel = ({
  refreshKey,
  children,
  ...props
}: TabPanelProps & { refreshKey: string | number | undefined }) => (
  <ReachTabPanel {...props}>
    {withRefreshKey(children, refreshKey)}
  </ReachTabPanel>
);

/** Only mount tab content when the tab is selected. */
export const LazyTabPanel = ({
  refreshKey,
  isSelected,
  children,
  ...props
}: TabPanelProps & {
  refreshKey: string | number | undefined;
  isSelected: boolean;
  children?: ReactNode;
}) => (
  <ReachTabPanel {...props}>
    {isSelected ? withRefreshKey(children, refreshKey) : null}
  </ReachTabPanel>
);
